import {
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

interface ComplianceCheck {
  passed: boolean;
  requiredDocuments: Array<{
    name: string;
    mandatory: boolean;
    present: boolean;
    location?: string;
  }>;
  missingMandatory: string[];
  missingOptional: string[];
}

interface ComplianceDetailsProps {
  data: ComplianceCheck;
}

export default function ComplianceDetails({ data }: ComplianceDetailsProps) {
  return (
    <Stack spacing={2}>
      {/* Missing Mandatory Documents Alert */}
      {data.missingMandatory.length > 0 && (
        <Alert severity="error">
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Missing Mandatory Documents:
          </Typography>
          <List dense disablePadding>
            {data.missingMandatory.map((doc, idx) => (
              <ListItem key={idx} disablePadding>
                <Typography variant="body2">• {doc}</Typography>
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* Missing Optional Documents Alert */}
      {data.missingOptional.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Missing Optional Documents:
          </Typography>
          <List dense disablePadding>
            {data.missingOptional.map((doc, idx) => (
              <ListItem key={idx} disablePadding>
                <Typography variant="body2">• {doc}</Typography>
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* Success Message */}
      {data.passed && (
        <Alert severity="success">
          All required documents are present and accounted for.
        </Alert>
      )}

      {/* Document Checklist */}
      {data.requiredDocuments.length > 0 && (
        <List>
          {data.requiredDocuments.map((doc, idx) => (
            <ListItem key={idx}>
              <ListItemIcon>
                {doc.present ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={doc.name}
                secondary={doc.location || (doc.present ? 'Present' : 'Missing')}
              />
              {doc.mandatory && !doc.present && (
                <Chip label="MANDATORY" color="error" size="small" />
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  );
}
